import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";

import ModalBody from "../../../common/ModalBody";
import ModalHeader from "../../../common/ModalHeader";
import ModalFooter from "../../../common/ModalFooter";
import { getCurrentDate } from "../../../../common/Validations";
import { fetchMasterListById } from "../../../../redux/feature/manufacturing_app/commonMasterListSlice";
import { InputField } from "../../../../common/formElements/InputField";
import ItemService from "../../../../services/commonService";
import { DropdownListField } from "../../../../common/formElements/DropdownListField";
import { toastFunc } from "../../../../components/common/util";
import Constants from "../../../../common/Constants";
import {
  getJobcardReportFooter,
  loadJobcardRevisingMetadata,
  REVISING_REPORT_TYPES,
} from "../../../../common/JobcardPrintUtils";

const QUALITY_CERTIFICATE_REPORT_TITLE = "TM/QC/F/01";

const selectOption = [
  {
    id: "Yes",
    name: "Yes",
  },
  {
    id: "No",
    name: "No",
  },
];

const firstNonEmpty = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  ) ?? "";

const normalizeJobcard = (jobcard) => String(jobcard ?? "").trim();

const QualityCertificateReport = (props) => {
  //const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [qcSizeCodes, setQCSizeCodes] = useState([]);
  const [boreDepth, setBoreDepth] = useState("");
  const [revisingMetadata, setRevisingMetadata] = useState({});
  const [dimensionsComplies, setDimesionComplies] = useState("No");
  const [boreDiameterComplies, setBoreDiameterComplies] = useState("No");
  const [wireDiameterComplies, setWireDiameterComplies] = useState("No");
  const [boreDepthComplies, setBoreDepthComplies] = useState("No");
  const [surfaceFinishComplies, setSurfaceFinishComplies] = useState("No");
  const [siliconisationComplies, setSiliconisationComplies] = useState("No");
  const [penetrationTestComplies, setPenetrationTestComplies] = useState("No");
  const [dimensionObservation, setDimensionObservation] = useState("");
  const [tempDimensionObservation, setTempDimensionObservation] = useState("");
  const [boreDiameterObservation, setBoreDiameterObservation] = useState("");
  const [tempBoreDiameterObservation, setTempBoreDiameterObservation] =
    useState("");
  const [wireDiameterObservation, setWireDiameterObservation] = useState("");
  const [tempWireDiameterObservation, setTempWireDiameterObservation] =
    useState("");
  const [boreDepthObservation, setBoreDepthObservation] = useState("");
  const [tempBoreDepthObservation, setTempBoreDepthObservation] = useState("");
  const [pentrationObservation, setPenetrationObservation] = useState("");
  const [tempPentrationObservation, setTempPentrationObservation] =
    useState("");
  const [batchTotalQuantity, setBatchTotalQuantity] = useState("");
  const [originalQuantityPerJobCard, setOriginalQuantityPerJobCard] =
    useState("");
  const [reportDate, setReportDate] = useState(getCurrentDate());
  const [approvedQuantity, setApprovedQuantity] = useState();
  const [parts, setParts] = useState();
  const [remarks, setRemarks] = useState("");

  const certificateData = useMemo(() => {
    const reportData = props.report || {};
    const rowData =
      props.row && !Array.isArray(props.row) ? props.row : {};
    const reportJobcard = normalizeJobcard(reportData.jobcard);
    const rowJobcard = normalizeJobcard(rowData.jobcard);
    const rowMatchesReport =
      !reportJobcard || (rowJobcard && rowJobcard === reportJobcard);
    const matchingRow = rowMatchesReport ? rowData : {};

    return {
      jobcard: firstNonEmpty(matchingRow.jobcard, reportData.jobcard),
      sizeCode: firstNonEmpty(matchingRow.sizeCode, reportData.sizeCode),
      sizeName: firstNonEmpty(matchingRow.sizeName, reportData.sizeName),
      batchTotalQuantity: firstNonEmpty(
        matchingRow.batchTotalQuantity,
        reportData.batchTotalQuantity,
      ),
      originalQuantityPerJobCard: firstNonEmpty(
        matchingRow.originalQuantityPerJobCard,
        reportData.originalQuantityPerJobCard,
      ),
      quantityPerJobCard: firstNonEmpty(
        matchingRow.quantityPerJobCard,
        reportData.approvedQuantity,
      ),
    };
  }, [props.report, props.row]);

  useEffect(() => {
    let param = {};
    param.url = "getSizeById";
    param.sizeCode = certificateData.sizeCode;
    setBatchTotalQuantity(certificateData.batchTotalQuantity);
    setOriginalQuantityPerJobCard(
      certificateData.originalQuantityPerJobCard,
    );
    setApprovedQuantity(certificateData.quantityPerJobCard);
    if (certificateData.sizeCode) {
      dispatch(fetchMasterListById(param)).then((result) => {
        setQCSizeCodes(result?.payload?.data?.qcSizeCode?.split("-"));
        setBoreDepth(result?.payload?.data?.boreDepth);
        const parts = result?.payload?.data?.reportTitle
          ?.split(",")
          .map((part) => part.trim());
        setParts(parts);
      });
      if (props.report) {
        const existingQCReport = props.report;
        setDimesionComplies(existingQCReport.dimensionsComplies);
        setBoreDiameterComplies(existingQCReport.boreDiameterComplies);
        setWireDiameterComplies(existingQCReport.wireDiameterComplies);
        setBoreDepthComplies(existingQCReport.boreDepthComplies);
        setSurfaceFinishComplies(existingQCReport.surfaceFinishComplies);
        setSiliconisationComplies(existingQCReport.siliconisationComplies);
        setPenetrationTestComplies(existingQCReport.penetrationTestComplies);
        setDimensionObservation(existingQCReport.dimensionObservation);
        setBoreDiameterObservation(existingQCReport.boreDiameterObservation);
        setWireDiameterObservation(existingQCReport.wireDiameterObservation);
        setBoreDepthObservation(existingQCReport.boreDepthObservation);
        setRemarks(existingQCReport.remarks);
        setPenetrationObservation(existingQCReport.pentrationObservation);
        setBatchTotalQuantity(existingQCReport.batchTotalQuantity);
        setOriginalQuantityPerJobCard(
          existingQCReport.originalQuantityPerJobCard,
        );
        setReportDate(existingQCReport.reportDate);
        setApprovedQuantity(existingQCReport.approvedQuantity);
      }
    }
    if (certificateData.jobcard) {
      loadJobcardRevisingMetadata(
        [certificateData],
        REVISING_REPORT_TYPES.QUALITY_CERTIFICATE,
      )
        .then(setRevisingMetadata)
        .catch((error) => {
          console.error("Error loading jobcard revising metadata:", error);
          setRevisingMetadata({});
        });
    } else {
      setRevisingMetadata({});
    }
  }, [certificateData, dispatch, props.report]);

  const generateQC = async (action) => {
    const param = {
      jobcard: certificateData.jobcard,
      sizeCode: certificateData.sizeCode,
      sizeName: certificateData.sizeName,
      qcSizeCodes: qcSizeCodes,
      boreDepth: boreDepth,
      reportTitle: QUALITY_CERTIFICATE_REPORT_TITLE,
      wireDiameterComplies: wireDiameterComplies,
      boreDepthComplies: boreDepthComplies,
      dimensionsComplies: dimensionsComplies,
      boreDiameterComplies: boreDiameterComplies,
      surfaceFinishComplies: surfaceFinishComplies,
      siliconisationComplies: siliconisationComplies,
      penetrationTestComplies: penetrationTestComplies,
      dimensionObservation: dimensionObservation,
      boreDiameterObservation: boreDiameterObservation,
      boreDepthObservation: boreDepthObservation,
      wireDiameterObservation: wireDiameterObservation,
      pentrationObservation: pentrationObservation,
      batchTotalQuantity: batchTotalQuantity
        ? batchTotalQuantity
        : certificateData.batchTotalQuantity,
      originalQuantityPerJobCard: originalQuantityPerJobCard
        ? originalQuantityPerJobCard
        : certificateData.originalQuantityPerJobCard,
      approvedQuantity: approvedQuantity
        ? approvedQuantity
        : certificateData.quantityPerJobCard,
      reportDate: getCurrentDate(),
      remarks: remarks,
      parts: parts,
    };
    await ItemService.generateQCReport(param).then((item) => {
      if (item?.status === 200) {
        props.onHide();
        toastFunc(
          Constants.MASTER.TOAST.success,
          t("msg_reports.quality_certificate.generate_success_message"),
          2000,
        );
      }
    });
  };

  const handleCancel = (event) => {
    setDimesionComplies();
    setBoreDiameterComplies();
    setWireDiameterComplies();
    setBoreDepthComplies();
    setSurfaceFinishComplies();
    setSiliconisationComplies();
    setPenetrationTestComplies();
    setDimensionObservation();
    setBoreDiameterObservation();
    setWireDiameterObservation();
    setBoreDepthObservation();
    setRemarks();
    setPenetrationObservation();
    setBatchTotalQuantity();
    setOriginalQuantityPerJobCard();
    setReportDate();
    setApprovedQuantity();
    setQCSizeCodes();
    setBoreDepth();
    setBatchTotalQuantity();
    setOriginalQuantityPerJobCard();
    setApprovedQuantity();
    props.onHide();
  };

  return (
    <Modal
      {...props}
      onHide={() => handleCancel()}
      animation={false}
      dialogClassName="modal-90w"
      backdrop="static"
    >
      <ModalHeader
        title={` ${t(
          "msg_reports.quality_certificate.quality_certificate_report_for_jobcard",
        )} - ${certificateData.jobcard} `}
      ></ModalHeader>
      <ModalBody>
        <div id="printable-area">
          <Row>
            <>
              <Col xs={12} sm={12} md={12} lg={12} className="mb-4">
                <table className="jobcard-content-table">
                  <thead>
                    <th colSpan="6">
                      <h4 className="telma-header">
                        TELMA MICRO NEEDLES PVT LTD
                      </h4>
                      <span className="telma-detail">
                        <p>
                          NO-56/6, MAJARA HOSAHALLI MAIN ROAD, <br></br>
                          DODDABALLAPURA INDUSTRIAL AREA,<br></br>
                          BANGALORE-561203
                        </p>
                      </span>
                    </th>
                  </thead>

                  <tbody>
                    <tr>
                      <td colSpan="6">
                        <h4 className="telma-header">QUALITY CERTIFICATE</h4>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2">Needle Code:</td>
                      <td colSpan="1">{certificateData.sizeCode || "-"}</td>
                      <td colSpan="1">Description:</td>
                      <td colSpan="2">{certificateData.sizeName || "-"}</td>
                    </tr>
                    <tr>
                      <td colSpan="2">Batch No:</td>
                      <td colSpan="1">
                        {certificateData.jobcard?.split("_")?.[0] || "-"}
                      </td>
                      <td colSpan="1">Batch Qty No:</td>
                      <td colSpan="2">
                        {originalQuantityPerJobCard
                          ? originalQuantityPerJobCard
                          : certificateData.originalQuantityPerJobCard}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2">Date:</td>
                      <td colSpan="1">
                        <span>
                          {reportDate ? reportDate : getCurrentDate()}
                        </span>
                      </td>
                      <td colSpan="1">Sample Qty</td>
                      <td colSpan="2">5</td>
                    </tr>
                    <tr>
                      <td colSpan="2"> </td>
                      <td colSpan="1"> </td>
                      <td colSpan="1"> </td>
                      <td colSpan="2"> </td>
                    </tr>
                    <tr>
                      <td colSpan="1">SI No.</td>
                      <td colSpan="1">Tests</td>
                      <td colSpan="1">Specifications</td>
                      <td colSpan="1">Tolerance</td>
                      <td colSpan="1">Observations</td>
                      <td>Complies</td>
                    </tr>
                    <tr>
                      <td rowSpan="6">1</td>
                      <td height={"80px"}>Dimensions And Curvature</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td>a. Length</td>
                      <td>{qcSizeCodes?.[1]}</td>
                      <td>
                        <i className="bi bi-plus-slash-minus pr-2"></i> 1.000 mm
                      </td>
                      <td>
                        <div>
                          <p className="m-0 p-0 pt-1 fw-datatable-row">
                            {dimensionObservation}{" "}
                          </p>
                        </div>
                        <div>
                          <span>
                            <input
                              type="text"
                              className="commLeftRightPadding"
                              width={5}
                              value={tempDimensionObservation}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setTempDimensionObservation(e.target.value);
                                }
                              }}
                            />
                            <i
                              className="bi bi-plus-circle iconTable pl-2"
                              onClick={() => {
                                if (dimensionObservation) {
                                  setDimensionObservation(
                                    tempDimensionObservation,
                                  );
                                } else {
                                  setDimensionObservation(
                                    tempDimensionObservation,
                                  );
                                }
                                setTempDimensionObservation("");
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={dimensionsComplies}
                          onChange={(value) => {
                            setDimesionComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>b. Bore diameter</td>
                      <td>{parseInt(qcSizeCodes?.[2]) * parseInt(10)}</td>
                      <td>
                        &nbsp;- 0.020 mm <br />+ 0.030mm
                      </td>
                      <td>
                        <div>
                          <p className="m-0 p-0 pt-1 fw-datatable-row">
                            {boreDiameterObservation}{" "}
                          </p>
                        </div>
                        <div>
                          <span>
                            <input
                              type="text"
                              className="commLeftRightPadding"
                              width={5}
                              value={tempBoreDiameterObservation}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setTempBoreDiameterObservation(
                                    e.target.value,
                                  );
                                }
                              }}
                            />
                            <i
                              className="bi bi-plus-circle iconTable pl-2"
                              onClick={() => {
                                if (boreDiameterObservation) {
                                  setBoreDiameterObservation(
                                    tempBoreDiameterObservation,
                                  );
                                } else {
                                  setBoreDiameterObservation(
                                    tempBoreDiameterObservation,
                                  );
                                }
                                setTempBoreDiameterObservation("");
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={boreDiameterComplies}
                          onChange={(value) => {
                            setBoreDiameterComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>c. Wire diameter</td>
                      <td>{parseInt(qcSizeCodes?.[3]) * parseInt(10)}</td>
                      <td>
                        <i className="bi bi-plus-slash-minus pr-2"></i> 0.020 mm
                      </td>
                      <td>
                        <div>
                          <p className="m-0 p-0 pt-1 fw-datatable-row">
                            {wireDiameterObservation}{" "}
                          </p>
                        </div>
                        <div>
                          <span>
                            <input
                              type="text"
                              className="commLeftRightPadding"
                              width={5}
                              value={tempWireDiameterObservation}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setTempWireDiameterObservation(
                                    e.target.value,
                                  );
                                }
                              }}
                            />
                            <i
                              className="bi bi-plus-circle iconTable pl-2"
                              onClick={() => {
                                if (wireDiameterObservation) {
                                  setWireDiameterObservation(
                                    tempWireDiameterObservation,
                                  );
                                } else {
                                  setWireDiameterObservation(
                                    tempWireDiameterObservation,
                                  );
                                }
                                setTempWireDiameterObservation("");
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={wireDiameterComplies}
                          onChange={(value) => {
                            setWireDiameterComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>d. Bore depth</td>
                      <td>{boreDepth}</td>
                      <td>
                        <i className="bi bi-plus-slash-minus pr-2"></i> 0.200 mm
                      </td>
                      <td>
                        <div>
                          <p className="m-0 p-0 pt-1 fw-datatable-row">
                            {boreDepthObservation}{" "}
                          </p>
                        </div>
                        <div>
                          <span>
                            <input
                              type="text"
                              className="commLeftRightPadding"
                              width={5}
                              value={tempBoreDepthObservation}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setTempBoreDepthObservation(e.target.value);
                                }
                              }}
                            />
                            <i
                              className="bi bi-plus-circle iconTable pl-2"
                              onClick={() => {
                                if (boreDepthObservation) {
                                  setBoreDepthObservation(
                                    tempBoreDepthObservation,
                                  );
                                } else {
                                  setBoreDepthObservation(
                                    tempBoreDepthObservation,
                                  );
                                }
                                setTempBoreDepthObservation("");
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={boreDepthComplies}
                          onChange={(value) => {
                            setBoreDepthComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td height={"80px"}>e. Curvature</td>
                      <td>Match With Curvature Template</td>
                      <td>Matching With Curvature Template</td>
                      <td>Complies With Curvature Template</td>
                      <td>Yes</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>Surface Finish</td>
                      <td>Smooth Finish</td>
                      <td>Pit Free Surface</td>
                      <td>Observed Pit Free Surface Finish</td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={surfaceFinishComplies}
                          onChange={(value) => {
                            setSurfaceFinishComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>3</td>
                      <td>Siliconisation</td>
                      <td>coating the surface of a needle with a thin layer</td>
                      <td>Aluminum Oxide powder Test</td>
                      <td>Aluminum Oxide powder adheres to the needles</td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={siliconisationComplies}
                          onChange={(value) => {
                            setSiliconisationComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>4</td>
                      <td>Penetration Test</td>
                      <td>Consistency Of Penetration</td>
                      <td>
                        <p>
                          Min-0.50Kgs <br></br>Max-2Kgs
                        </p>
                      </td>
                      <td>
                        <div>
                          <p className="m-0 p-0 pt-1 fw-datatable-row">
                            {pentrationObservation}{" "}
                          </p>
                        </div>
                        <div>
                          <span>
                            <input
                              type="text"
                              className="commLeftRightPadding"
                              width={5}
                              value={tempPentrationObservation}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setTempPentrationObservation(e.target.value);
                                }
                              }}
                            />
                            <i
                              className="bi bi-plus-circle iconTable pl-2"
                              onClick={() => {
                                if (pentrationObservation) {
                                  setPenetrationObservation(
                                    tempPentrationObservation,
                                  );
                                } else {
                                  setPenetrationObservation(
                                    tempPentrationObservation,
                                  );
                                }
                                setTempPentrationObservation("");
                              }}
                            ></i>
                          </span>
                        </div>
                      </td>
                      <td>
                        <DropdownListField
                          id="jobcardStatusId"
                          name="name"
                          data={selectOption}
                          value={penetrationTestComplies}
                          onChange={(value) => {
                            setPenetrationTestComplies(value?.id);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>STATUS</td>
                      <td>
                        <span className="fw-bold-m">APPROVED</span>
                      </td>
                      <td colSpan="2">APPROVED QY NOS:</td>
                      <td colSpan="2">
                        <span className="fw-bold-m">
                          {approvedQuantity
                            ? approvedQuantity
                            : certificateData.quantityPerJobCard}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="1">Remarks</td>
                      <td colSpan="5">
                        <InputField
                          value={remarks}
                          className="commLeftRightPadding"
                          onChange={(e) => {
                            if (e.target.value) {
                              setRemarks(e.target.value);
                            }
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6" height="50">
                        <b>
                          Note: Length And Bore Depth are in Millimeters, Bore Diameter And Wire Diameter are in Microns.
                        </b>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6">
                        <Row>
                          <Col md={9}>
                            <div>
                              Inspected by:
                              <img
                                src="assets/img/inspected.png"
                                alt="Inspected"
                                className="qc-sign-1"
                              />
                            </div>

                            <div>
                              Date:
                              <span>
                                &nbsp;
                                {reportDate ? reportDate : getCurrentDate()}
                              </span>
                            </div>
                          </Col>

                          <Col md={3}>
                            <div>
                              Approved by:
                              <img
                                src="assets/img/approved.png"
                                alt="Approved"
                                className="qc-sign-2"
                              />
                            </div>
                            <div>
                              Date:
                              <span>
                                &nbsp;
                                {reportDate ? reportDate : getCurrentDate()}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Col>
              <div className="report-title-line">
                {(() => {
                  const footer = getJobcardReportFooter({
                    jobcard: certificateData.jobcard,
                    revisingMetadata,
                    fixedReportTitle: QUALITY_CERTIFICATE_REPORT_TITLE,
                  });

                  return (
                    <>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        {footer.reportTitle}
                      </Col>
                      <Col xs={12} sm={3} md={3} lg={3}>
                        Rev No: {footer.reviseNumber}
                      </Col>
                      <Col xs={12} sm={3} md={3} lg={3} className="text-end">
                        Date: {footer.reviseDate}
                      </Col>
                    </>
                  );
                })()}
              </div>
            </>
          </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        <Row>
          <Col
            className="d-flex justify-content-center"
            lg={6}
            md={6}
            sm={6}
            xs={12}
          >
            <Button
              type="button"
              variant="success"
              size="lg"
              className="btn btn-success"
              onClick={() => generateQC()}
            >
              {t("msg_common.save")}
            </Button>
          </Col>
          <Col
            className="d-flex justify-content-center commLeftRightPadding"
            lg={6}
            md={6}
            sm={6}
            xs={12}
          >
            <Button
              type="button"
              variant="danger"
              size="lg"
              className="btn btn-danger"
              onClick={handleCancel}
            >
              {t("msg_common.cancel")}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default QualityCertificateReport;
